import { apiClient } from "./api";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Incident filters interface
 */
export interface IncidentFilters {
  status?: string;
  priority?: string;
  category?: string;
  assigned_to?: string;
  limit?: number;
}

/**
 * Incident interface
 */
export interface Incident {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category?: string;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  [key: string]: any;
}

/**
 * Incident statistics interface
 */
export interface IncidentStats {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  avgResolutionTime?: number;
  [key: string]: any;
}

/**
 * AI Recommendation interface
 */
export interface Recommendation {
  id: string;
  incidentId: string;
  description: string;
  confidence: number;
  validated?: boolean;
  implemented?: boolean;
  [key: string]: any;
}

/**
 * Knowledge Base interface
 */
export interface KnowledgeBase {
  id: string;
  name: string;
  category: string;
  content?: string;
  procedures?: any[];
  [key: string]: any;
}

/**
 * RAG Agent interface
 */
export interface RAGAgent {
  id: string;
  name: string;
  description: string;
  status: string;
  model?: string;
  [key: string]: any;
}

/**
 * Incident Category interface
 */
export interface IncidentCategoryResponse {
  id: string;
  category: string;
  name: string;
  description: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// API Functions
// ============================================================================

export const incidentApi = {
  /**
   * Get all incidents with filtering
   */
  getIncidents: async (filters: IncidentFilters = {}): Promise<Incident[]> => {
    const params: Record<string, any> = {};
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.category) params.category = filters.category;
    if (filters.assigned_to) params.assigned_to = filters.assigned_to;
    if (filters.limit) params.limit = filters.limit;

    const response = await apiClient.get<Incident[]>("/incidents", { params });
    return response.data;
  },

  /**
   * Get incident statistics
   */
  getIncidentStats: async (): Promise<IncidentStats> => {
    const response = await apiClient.get<IncidentStats>("/incidents/stats");
    return response.data;
  },

  /**
   * Get incident by ID
   */
  getIncident: async (incidentId: string): Promise<Incident> => {
    const response = await apiClient.get<Incident>(`/incidents/${incidentId}`);
    return response.data;
  },

  /**
   * Create a new incident
   */
  createIncident: async (incident: Partial<Incident>): Promise<Incident> => {
    const response = await apiClient.post<Incident>("/incidents", incident);
    return response.data;
  },

  /**
   * Update incident
   */
  updateIncident: async (incidentId: string, updates: Partial<Incident>): Promise<Incident> => {
    const response = await apiClient.put<Incident>(`/incidents/${incidentId}`, updates);
    return response.data;
  },

  /**
   * Assign AI agent to incident
   */
  assignAIAgent: async (incidentId: string): Promise<any> => {
    const response = await apiClient.post(`/incidents/${incidentId}/assign-ai-agent`);
    return response.data;
  },

  /**
   * Get AI recommendations for incident
   */
  getRecommendations: async (incidentId: string): Promise<Recommendation[]> => {
    const response = await apiClient.get<Recommendation[]>(
      `/incidents/${incidentId}/recommendations`
    );
    return response.data;
  },

  /**
   * Validate AI recommendation
   */
  validateRecommendation: async (
    recommendationId: string,
    validated: boolean,
    feedback: string | null = null
  ): Promise<Recommendation> => {
    const response = await apiClient.post<Recommendation>(
      `/recommendations/${recommendationId}/validate`,
      {
        validated,
        feedback,
      },
      {
        params: { validated, feedback },
      }
    );
    return response.data;
  },

  /**
   * Mark recommendation as implemented
   */
  implementRecommendation: async (recommendationId: string): Promise<Recommendation> => {
    const response = await apiClient.post<Recommendation>(
      `/recommendations/${recommendationId}/implement`
    );
    return response.data;
  },

  /**
   * Create mock data for testing
   */
  createMockIncidents: async (): Promise<any> => {
    const response = await apiClient.post("/mock-data/incidents");
    return response.data;
  },

  /**
   * Get incident categories
   * Note: This endpoint is on the external API, not the local backend
   */
  getIncidentCategories: async (): Promise<IncidentCategoryResponse[]> => {
    try {
      // Use external API endpoint directly since categories endpoint may not exist on local backend
      const externalApiUrl = "/incidents/categories/";
      const response = await fetch(externalApiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching incident categories:", error);
      // Fallback: try local backend if external API fails
      try {
        const response = await apiClient.get<IncidentCategoryResponse[]>("/incidents/categories/");
        return response.data;
      } catch (localError) {
        console.error("Local backend also failed:", localError);
        return [];
      }
    }
  },
};

export const ragApi = {
  /**
   * Get knowledge bases
   */
  getKnowledgeBases: async (category: string | null = null): Promise<KnowledgeBase[]> => {
    const params = category ? { category } : {};
    const response = await apiClient.get<KnowledgeBase[]>("/knowledge-bases", { params });
    return response.data;
  },

  /**
   * Create knowledge base
   */
  createKnowledgeBase: async (knowledgeBase: Partial<KnowledgeBase>): Promise<KnowledgeBase> => {
    const response = await apiClient.post<KnowledgeBase>("/knowledge-bases", knowledgeBase);
    return response.data;
  },

  /**
   * Get RAG agents
   */
  getRAGAgents: async (): Promise<RAGAgent[]> => {
    const response = await apiClient.get<RAGAgent[]>("/rag-agents");
    return response.data;
  },

  /**
   * Create RAG agent
   */
  createRAGAgent: async (agent: Partial<RAGAgent>): Promise<RAGAgent> => {
    const response = await apiClient.post<RAGAgent>("/rag-agents", agent);
    return response.data;
  },

  /**
   * Create mock knowledge bases
   */
  createMockKnowledgeBases: async (): Promise<any> => {
    const response = await apiClient.post("/mock-data/knowledge-bases");
    return response.data;
  },

  /**
   * Create mock RAG agents
   */
  createMockRAGAgents: async (): Promise<any> => {
    const response = await apiClient.post("/mock-data/rag-agents");
    return response.data;
  },

  /**
   * Get knowledge base details
   */
  getKnowledgeBaseDetails: async (kbId: string): Promise<KnowledgeBase> => {
    const response = await apiClient.get<KnowledgeBase>(`/knowledge-bases/${kbId}`);
    return response.data;
  },

  /**
   * Update knowledge base
   */
  updateKnowledgeBase: async (
    kbId: string,
    updates: Partial<KnowledgeBase>
  ): Promise<KnowledgeBase> => {
    const response = await apiClient.put<KnowledgeBase>(`/knowledge-bases/${kbId}`, updates);
    return response.data;
  },

  /**
   * Add procedure to knowledge base
   */
  addProcedureToKnowledgeBase: async (kbId: string, procedure: any): Promise<any> => {
    const response = await apiClient.post(`/knowledge-bases/${kbId}/procedures`, procedure);
    return response.data;
  },

  /**
   * Get RAG agent details
   */
  getRAGAgentDetails: async (agentId: string): Promise<RAGAgent> => {
    const response = await apiClient.get<RAGAgent>(`/rag-agents/${agentId}`);
    return response.data;
  },

  /**
   * Update RAG agent (fine-tuning)
   */
  updateRAGAgent: async (agentId: string, updates: Partial<RAGAgent>): Promise<RAGAgent> => {
    const response = await apiClient.put<RAGAgent>(`/rag-agents/${agentId}`, updates);
    return response.data;
  },
};

// ============================================================================
// Constants
// ============================================================================

export const INCIDENT_PRIORITIES = {
  CRITICAL: "CRITICAL",
  HIGH: "HIGH",
  MEDIUM: "MEDIUM",
  LOW: "LOW",
} as const;

export const INCIDENT_STATUSES = {
  NEW: "NEW",
  IN_PROGRESS: "IN_PROGRESS",
  PENDING_VALIDATION: "PENDING_VALIDATION",
  RESOLVED: "RESOLVED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED",
} as const;

export const INCIDENT_CATEGORIES = {
  SERVER_DOWNTIME: "SERVER_DOWNTIME",
  DATABASE_ISSUE: "DATABASE_ISSUE",
  APPLICATION_ERROR: "APPLICATION_ERROR",
  NETWORK_CONNECTIVITY: "NETWORK_CONNECTIVITY",
  PERFORMANCE_DEGRADATION: "PERFORMANCE_DEGRADATION",
  SECURITY_INCIDENT: "SECURITY_INCIDENT",
  CONFIGURATION_ERROR: "CONFIGURATION_ERROR",
  HARDWARE_FAILURE: "HARDWARE_FAILURE",
} as const;

export const CATEGORY_LABELS: Record<string, string> = {
  [INCIDENT_CATEGORIES.SERVER_DOWNTIME]: "Server Downtime",
  [INCIDENT_CATEGORIES.DATABASE_ISSUE]: "Database Issue",
  [INCIDENT_CATEGORIES.APPLICATION_ERROR]: "Application Error",
  [INCIDENT_CATEGORIES.NETWORK_CONNECTIVITY]: "Network Connectivity",
  [INCIDENT_CATEGORIES.PERFORMANCE_DEGRADATION]: "Performance Degradation",
  [INCIDENT_CATEGORIES.SECURITY_INCIDENT]: "Security Incident",
  [INCIDENT_CATEGORIES.CONFIGURATION_ERROR]: "Configuration Error",
  [INCIDENT_CATEGORIES.HARDWARE_FAILURE]: "Hardware Failure",
};

// Type exports for constants
export type IncidentPriority = (typeof INCIDENT_PRIORITIES)[keyof typeof INCIDENT_PRIORITIES];
export type IncidentStatus = (typeof INCIDENT_STATUSES)[keyof typeof INCIDENT_STATUSES];
export type IncidentCategory = (typeof INCIDENT_CATEGORIES)[keyof typeof INCIDENT_CATEGORIES];
